import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { API_URL, formatDate } from '../lib/utils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { FileText, Download, ChevronDown, X, BarChart3, Table as TableIcon, FileSpreadsheet, RefreshCw, ArrowUpDown, Settings, GripVertical, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { REPORT_MODULES, MODULE_ENDPOINTS } from '../components/reports/reportModules';
import { isTimeLogAggregationActive } from '../components/reports/aggregateTimeLogs';
import { buildReportData } from '../components/reports/buildReportData';
import { exportReportToCSV, exportReportToPDF } from '../components/reports/exportHelpers';

const formatCurrency = (amount) => {
  return `INR ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount)}`;
};

export const ReportsPage = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);

  // Custom report builder state - supports multiple modules
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ field: null, direction: 'asc' });
  const [filterConfig, setFilterConfig] = useState({ field: '', operator: '', value: '' });
  const [reportName, setReportName] = useState('Custom Report');

  // All raw data for custom reports
  const [allData, setAllData] = useState({});

  useEffect(() => {
    setLoading(false);
  }, []);

  // Fetch data for custom report builder.
  // `force` bypasses the in-memory cache - used by Generate to ensure freshly-edited
  // values (e.g. a newly-set project_code) show up without a page reload.
  const fetchModuleData = async (module, force = false) => {
    if (!force && allData[module]) return allData[module];
    try {
      const response = await axios.get(`${API_URL}${MODULE_ENDPOINTS[module]}`, { withCredentials: true });
      const data = response.data;
      setAllData(prev => ({ ...prev, [module]: data }));
      return data;
    } catch (error) {
      toast.error(`Failed to load ${module} data`);
      return [];
    }
  };

  // Defensive enrichment: if time_logs rows are missing `project_code` (e.g. stale backend
  // that hasn't been redeployed since the project_code enrichment was added), fill it
  // client-side by joining against the `projects` collection. We always fetch projects
  // here so this fallback is reliable.
  const enrichTimeLogsWithProjectCode = (timeLogs, projects) => {
    if (!Array.isArray(timeLogs) || timeLogs.length === 0) return timeLogs;
    const hasAnyCode = timeLogs.some(l => l.project_code);
    if (hasAnyCode) return timeLogs; // backend already enriched
    const codeByProjectId = {};
    (projects || []).forEach(p => {
      if (p.project_id) codeByProjectId[p.project_id] = p.project_code || '';
    });
    return timeLogs.map(l => ({
      ...l,
      project_code: l.project_code || codeByProjectId[l.project_id] || ''
    }));
  };

  const handleModuleChange = async (module) => {
    setSelectedModule(module);
    if (module && !selectedModules.includes(module)) {
      setSelectedModules(prev => [...prev, module]);
    }
  };

  const handleRemoveModule = (module) => {
    setSelectedModules(prev => prev.filter(m => m !== module));
    const moduleFieldIds = (REPORT_MODULES[module]?.fields || []).map(f => `${module}.${f.id}`);
    setSelectedFields(prev => prev.filter(f => !moduleFieldIds.includes(f)));
    if (selectedModule === module) {
      setSelectedModule(selectedModules.find(m => m !== module) || '');
    }
  };

  const handleAddField = (fieldId) => {
    const prefixed = `${selectedModule}.${fieldId}`;
    if (!selectedFields.includes(prefixed)) {
      setSelectedFields([...selectedFields, prefixed]);
    }
  };

  const handleRemoveField = (fieldId) => {
    setSelectedFields(selectedFields.filter(f => f !== fieldId));
  };

  const handleMoveField = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    setSelectedFields(prev => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const generateReport = async () => {
    if (selectedModules.length === 0 || selectedFields.length === 0) {
      toast.error('Please select at least one module and one field');
      return;
    }
    setReportLoading(true);
    try {
      const modulesData = {};
      // Always force-refresh on Generate so newly-edited values (e.g. project codes)
      // show up immediately without a page reload.
      for (const mod of selectedModules) {
        modulesData[mod] = await fetchModuleData(mod, true);
      }

      // If time_logs is in the report and project_code field is requested, ensure
      // time_logs rows carry project_code. Fetch projects as a join source if not already loaded.
      const needsProjectCode = selectedModules.includes('time_logs') &&
        selectedFields.includes('time_logs.project_code');
      if (needsProjectCode) {
        const projectsData = modulesData.projects || await fetchModuleData('projects', true);
        modulesData.time_logs = enrichTimeLogsWithProjectCode(modulesData.time_logs, projectsData);
      }

      const built = buildReportData({ selectedModules, selectedFields, modulesData });
      setReportData(built);
      toast.success('Report generated');
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setReportLoading(false);
    }
  };

  // Filter and sort report data
  const processedReportData = useMemo(() => {
    let data = [...reportData];
    if (filterConfig.field && filterConfig.operator && filterConfig.value) {
      data = data.filter(row => {
        const value = row[filterConfig.field];
        const fv = filterConfig.value;
        switch (filterConfig.operator) {
          case 'equals': return String(value).toLowerCase() === String(fv).toLowerCase();
          case 'contains': return String(value).toLowerCase().includes(String(fv).toLowerCase());
          case 'gt': return Number(value) > Number(fv);
          case 'lt': return Number(value) < Number(fv);
          case 'gte': return Number(value) >= Number(fv);
          case 'lte': return Number(value) <= Number(fv);
          default: return true;
        }
      });
    }
    if (sortConfig.field) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.field];
        const bVal = b[sortConfig.field];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [reportData, filterConfig, sortConfig]);

  const handleSort = (fieldId) => {
    setSortConfig(prev => ({
      field: fieldId,
      direction: prev.field === fieldId && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getFieldLabel = (fieldId) => {
    const [mod, fId] = fieldId.split('.');
    const moduleFields = REPORT_MODULES[mod]?.fields || [];
    const field = moduleFields.find(f => f.id === fId);
    const prefix = selectedModules.length > 1 ? `${REPORT_MODULES[mod]?.label || mod}: ` : '';
    return `${prefix}${field?.label || fId}`;
  };

  const exportToCSV = () =>
    exportReportToCSV({ rows: processedReportData, selectedFields, getFieldLabel, reportName });

  const exportToPDF = () =>
    exportReportToPDF({ rows: processedReportData, selectedFields, selectedModules, getFieldLabel, reportName });

  const formatCellValue = (value, fieldId) => {
    if (value == null || value === '') return '-';
    const [mod, fId] = fieldId.split('.');
    const moduleFields = REPORT_MODULES[mod]?.fields || [];
    const field = moduleFields.find(f => f.id === fId);

    if (fieldId === 'time_logs.duration_minutes') {
      if (value === '-' || value === '') return '-';
      const mins = Number(value) || 0;
      const hrs = Math.floor(mins / 60);
      const rem = mins % 60;
      return hrs > 0 ? `${hrs}h ${rem}m` : `${rem}m`;
    }
    if ((field?.type === 'number' && fieldId.includes('amount')) || fieldId.endsWith('.budget')) {
      return formatCurrency(value);
    }
    if (field?.type === 'boolean') return value ? 'Yes' : 'No';
    if (field?.type === 'date' && value) return formatDate(value);
    return String(value);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="reports-page" className="pt-12">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Reports & Analytics</h1>
          <p className="text-base text-muted-foreground">Generate insights and custom reports from your data</p>
        </div>
      </div>

      <Tabs defaultValue="custom-report" className="space-y-6">
        <TabsList>
          <TabsTrigger value="custom-report">Report Builder</TabsTrigger>
        </TabsList>

        {/* Custom Report Builder Tab */}
        <TabsContent value="custom-report" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Field Selection Panel */}
            <Card className="p-4 lg:col-span-1">
              <h3 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5" /> Data Source
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Add Data Module</label>
                  <Select value={selectedModule || '__none__'} onValueChange={(v) => handleModuleChange(v === '__none__' ? '' : v)}>
                    <SelectTrigger data-testid="reports-module-select-trigger">
                      <SelectValue placeholder="Choose data source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">-- Select Module --</SelectItem>
                      {Object.entries(REPORT_MODULES).map(([key, module]) => (
                        <SelectItem key={key} value={key} data-testid={`reports-module-option-${key}`}>{module.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Selected modules badges */}
                {selectedModules.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Active Modules</label>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {selectedModules.map(mod => (
                        <Badge key={mod} variant="secondary" className="flex items-center gap-1 text-xs">
                          {REPORT_MODULES[mod]?.label}
                          <button onClick={() => handleRemoveModule(mod)}>
                            <X className="h-3 w-3 hover:text-destructive" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    {selectedModules.length > 1 && (
                      <p className="text-xs text-muted-foreground mb-2">Cross-module report: data joined by project/user ID</p>
                    )}
                  </div>
                )}

                {selectedModule && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {REPORT_MODULES[selectedModule]?.label} Fields
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">Click to add to report</p>
                    <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="reports-field-list">
                      {REPORT_MODULES[selectedModule]?.fields.map(field => {
                        const prefixed = `${selectedModule}.${field.id}`;
                        return (
                          <button
                            key={field.id}
                            data-testid={`reports-field-add-${selectedModule}.${field.id}`}
                            className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                              selectedFields.includes(prefixed)
                                ? 'bg-primary/20 text-primary'
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => handleAddField(field.id)}
                            disabled={selectedFields.includes(prefixed)}
                          >
                            <div className="flex items-center justify-between">
                              <span>{field.label}</span>
                              {selectedFields.includes(prefixed) && (
                                <Badge variant="outline" className="text-xs">Added</Badge>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            {/* Report Configuration & Preview */}
            <div className="lg:col-span-3 space-y-4">
              {/* Selected Fields */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-heading font-semibold flex items-center gap-2">
                    <TableIcon className="h-5 w-5" /> Report Columns
                  </h3>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={reportName}
                      onChange={(e) => setReportName(e.target.value)}
                      className="w-48"
                      placeholder="Report Name"
                    />
                    <Button onClick={generateReport} disabled={reportLoading || selectedFields.length === 0} data-testid="reports-generate-btn">
                      <RefreshCw className={`mr-2 h-4 w-4 ${reportLoading ? 'animate-spin' : ''}`} />
                      Generate
                    </Button>
                  </div>
                </div>

                {selectedFields.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Select fields from the left panel to build your report
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {selectedFields.map((fieldId, idx) => (
                      <Badge 
                        key={fieldId} 
                        variant="secondary" 
                        className="px-3 py-1.5 flex items-center gap-2 cursor-move"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('fieldIndex', idx.toString())}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          const fromIndex = parseInt(e.dataTransfer.getData('fieldIndex'));
                          handleMoveField(fromIndex, idx);
                        }}
                      >
                        <GripVertical className="h-3 w-3 text-muted-foreground" />
                        {getFieldLabel(fieldId)}
                        <button onClick={() => handleRemoveField(fieldId)}>
                          <X className="h-3 w-3 hover:text-destructive" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </Card>

              {/* Filter & Sort */}
              {reportData.length > 0 && (
                <Card className="p-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Filter className="h-4 w-4 text-muted-foreground" />
                      <Select value={filterConfig.field || '__none__'} onValueChange={(v) => setFilterConfig(prev => ({ ...prev, field: v === '__none__' ? '' : v }))}>
                        <SelectTrigger className="w-36" data-testid="reports-filter-field">
                          <SelectValue placeholder="Filter by" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">No Filter</SelectItem>
                          {selectedFields.map(f => (
                            <SelectItem key={f} value={f}>{getFieldLabel(f)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {filterConfig.field && (
                        <>
                          <Select value={filterConfig.operator || '__none__'} onValueChange={(v) => setFilterConfig(prev => ({ ...prev, operator: v === '__none__' ? '' : v }))}>
                            <SelectTrigger className="w-32" data-testid="reports-filter-operator">
                              <SelectValue placeholder="Operator" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">Select</SelectItem>
                              <SelectItem value="equals">Equals</SelectItem>
                              <SelectItem value="contains">Contains</SelectItem>
                              <SelectItem value="gt">Greater than</SelectItem>
                              <SelectItem value="lt">Less than</SelectItem>
                              <SelectItem value="gte">≥</SelectItem>
                              <SelectItem value="lte">≤</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input 
                            value={filterConfig.value}
                            onChange={(e) => setFilterConfig(prev => ({ ...prev, value: e.target.value }))}
                            className="w-32"
                            placeholder="Value"
                            data-testid="reports-filter-value"
                          />
                        </>
                      )}
                    </div>
                    <div className="flex-1"></div>
                    <p className="text-sm text-muted-foreground">
                      {processedReportData.length} of {reportData.length} records
                    </p>
                  </div>
                </Card>
              )}

              {/* Report Preview Table */}
              {processedReportData.length > 0 && (
                <Card className="p-4">
                  {isTimeLogAggregationActive(selectedModules, selectedFields) && (
                      <div
                        className="mb-3 p-2 bg-blue-500/10 text-blue-700 text-xs rounded flex items-center gap-2"
                        data-testid="time-log-aggregation-notice"
                      >
                        <span className="font-semibold">Aggregated:</span>
                        <span>
                          Time totals are grouped per project/team member combination. Add the <strong>Date</strong> field to see per-day rows instead.
                        </span>
                      </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-heading font-semibold">{reportName}</h3>
                    <div className="flex gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" data-testid="reports-export-btn">
                            <Download className="mr-2 h-4 w-4" />
                            Export
                            <ChevronDown className="ml-2 h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={exportToCSV} data-testid="reports-export-csv">
                            <FileSpreadsheet className="mr-2 h-4 w-4" />
                            Export as CSV
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={exportToPDF} data-testid="reports-export-pdf">
                            <FileText className="mr-2 h-4 w-4" />
                            Export as PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <div className="overflow-x-auto max-h-96">
                    <Table data-testid="reports-preview-table">
                      <TableHeader>
                        <TableRow>
                          {selectedFields.map(fieldId => (
                            <TableHead 
                              key={fieldId}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleSort(fieldId)}
                              data-testid={`reports-th-${fieldId}`}
                            >
                              <div className="flex items-center gap-1">
                                {getFieldLabel(fieldId)}
                                <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {processedReportData.slice(0, 50).map((row, idx) => (
                          <TableRow
                            key={row._source_module ? `${row._source_module}-${idx}` : `row-${idx}`}
                            data-testid={`reports-row-${idx}`}
                          >
                            {selectedFields.map(fieldId => (
                              <TableCell key={fieldId} data-testid={`reports-cell-${idx}-${fieldId}`}>
                                {formatCellValue(row[fieldId], fieldId)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {processedReportData.length > 50 && (
                    <p className="text-sm text-muted-foreground text-center mt-4">
                      Showing 50 of {processedReportData.length} records. Export for full data.
                    </p>
                  )}
                </Card>
              )}

              {reportData.length === 0 && selectedFields.length > 0 && (
                <Card className="p-12 text-center">
                  <BarChart3 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-heading font-semibold mb-2">Ready to Generate</h3>
                  <p className="text-muted-foreground">Click "Generate" to create your custom report</p>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Standard Reports Tab removed - Report Builder is the only view */}
      </Tabs>
    </div>
  );
};
